import React, { Component } from 'react';
import Autocomplete from '../autocomplete';
import MultipleCheckboxs from './multipleCheckboxs';
import strings from '../../locales';

class MultipleTagsInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showPopup: false,
      tagsSelectedMultiple: {},
      currentTagsSelectedMultiple: {},
      actionButton: ''
    };
  }

  componentDidMount() {
    this.initCategories();
  }

  /**
   * Maps categories to include two new attributes used in the MultipleCheckbos control: label and value.
   * @returns {Array} An array of categories with added 'label' and 'value' attributes in their tags.
   */
  initCategories = () => {
    const { categories } = this.props;

    categories.map((category) => {
      const { tags } = category;

      return tags.map((item) => {
        item.label = item.tag;
        item.value = item.tag;
        return item;
      });
    });
  }

  /**
   * This method handles the display of a popup window.
   * @param {string} actionButton - The name of the button that triggers the popup.
   */
  handleShowPopup = (actionButton) => {
    let tagsSelectedMultiple = this.state.tagsSelectedMultiple;

    // if the Cancel button is pressed, it keeps the previous filters
    if (actionButton === 'cancel') {
      tagsSelectedMultiple = this.state.currentTagsSelectedMultiple;
    }

    this.setState({
      showPopup: !this.state.showPopup,
      tagsSelectedMultiple,
      actionButton
    });
  };

  /**
   * This method handles the submission and display of a popup window.
   * @param {object} event - The event object that contains the data to be submitted.
   */
  handleShowPopupAndSubmit = (event) => {
    const actionButton = 'apply';

    // it adds an attribute that determines from which list it was sent (multicheckboxs or simple list)
    if (this.props.isMultiple) {
      event = this.getTagsMultiple();
    }
    event.multipleTagsInputIsMultiple = this.props.isMultiple;

    // it makes a copy of the multiple tags selected, in case the Cancel button is pressed and the previous filters must be kept
    this.setState({
      currentTagsSelectedMultiple: {
        ...this.state.tagsSelectedMultiple
      },
      actionButton
    });

    this.props.handleSubmit(event);
    this.handleShowPopup(actionButton);
  };

  /**
   * This method handles the change of multiple checkboxes and updates the state accordingly.
   * @param {object} event - The event object that contains the alias and the selected options of the checkboxes.
   */
  handleChangeMultipleCheckboxs = (event) => {
    let changeState = {
      tagsSelectedMultiple: {
        ...this.state.tagsSelectedMultiple,
        [event.alias]: event.selectedOptions
      }
    };

    // if it is doing an initial rendering, then it assigns the values to the current
    if (event.actionButton === 'init') {
      changeState = {
        ...changeState,
        currentTagsSelectedMultiple: {
          ...this.state.currentTagsSelectedMultiple,
          [event.alias]: event.selectedOptions
        }
      };
    }

    this.setState(changeState);
  };

  /**
   * This method returns an array of tags based on the selected options of multiple checkboxes.
   * @returns {object} An object with a tags property that contains an array of strings.
   */
  getTagsMultiple = () => {
    const { tagsSelectedMultiple } = this.state;
    let tags = [];

    // gets the list of keys to iterate over the object
    const keys = Object.keys(tagsSelectedMultiple);

    // iterates over the keys to get the selected tags
    keys.map((key) => {
      const tagsSelected = tagsSelectedMultiple[key];

      // if it selects ALL the items, then it sends only the text "ALL"
      if (tagsSelected.some((o) => o.value === '*')) {
        tags = [...tags, strings.all];
      } else {
        tags = [...tags, ...tagsSelected.map((tag) => tag.value)];
      }
    });

    return {
      tags
    };
  };

  /**
   * This method returns an array of arrays of categories based on the selected options of multiple checkboxes.
   * @returns {object} An object with a property categoriesSlices that contains an array of arrays of strings, and a property categoriesSliceSize that contains an integer.
   */
  getCategoriesSlices = () => {
    const { categories } = this.props;

    let categoriesSlices = [];
    let categoriesSliceSize = (categories.length <= 5 || categories.length > 10) ? 5 : Math.round(categories.length / 2);

    for (let i = 0; i < categories.length; i += categoriesSliceSize) {
      categoriesSlices.push(categories.slice(i, i + categoriesSliceSize));
    }

    return {
      categoriesSlices,
      categoriesSliceSize
    };
  };

  /**
   * Generates text descriptions for the selected filters based on categories and filter criteria.
   * @returns {Object} An object containing the selected filters text and tooltip text.
   */
  getFiltersText = () => {
    const { categories, filteredTags, language, isMultiple } = this.props;
    let selectedFiltersText = '';
    let tooltipText = '';
    const arrFiltersText = [];
    const arrTooltipText = [];
    const MAX_TAGS_SHOW_LABEL = 3;

    // iterate through the categories to find selected items
    categories.map((category) => {
      let tagsSelected = [];
      selectedFiltersText = '';
      tooltipText = '';

      // iterate through previously selected filters to determine which ones match the tags of the category
      filteredTags.map((filter) => {
        if (category.tags.some((o) => o.tag === filter)) {
          tagsSelected.push(filter);
        }
      });

      // if no selected tags are found in the filters, search in state.tagsSelectedMultiple, only if there were previously selected tags
      if (filteredTags.length > 0 && tagsSelected.length === 0) {
        const listStateTagSelected = this.state.tagsSelectedMultiple[category.name];
        if (listStateTagSelected) {
          tagsSelected = listStateTagSelected
            .filter((tag) => tag.value !== '*')
            .map((tag) => tag.label);
        }
      }

      // if selected tags are found, construct the output text
      if (tagsSelected.length > 0) {
        // if the component is marked as isMultiple === true, build grouped text with the selected tags
        if (isMultiple) {
          selectedFiltersText = `${language[category.name.toLowerCase()]}: `;
          tooltipText = `<b>${language[category.name.toLowerCase()]}</b>: `;

          if (tagsSelected.length <= MAX_TAGS_SHOW_LABEL) {
            selectedFiltersText += tagsSelected.join(' / ');
            tooltipText += this.getTruncateTooltip(tagsSelected.join(', '));
          } else {
            // if all items are selected, then return the text "ALL"
            if (category.tags.length === tagsSelected.length) {
              selectedFiltersText = '';
              tooltipText += strings.all;
            } else {
              // get the first "MAX_TAGS_SHOW_LABEL" records from the array excluding the "ALL" item if it exists
              const tagsSelectedWithoutAll = tagsSelected.filter((tag) => !tag.toUpperCase().includes('TODOS') && !tag.toUpperCase().includes('ALL'));
              const sliceTagsSelected = tagsSelectedWithoutAll.slice(0, MAX_TAGS_SHOW_LABEL);
              selectedFiltersText += `${sliceTagsSelected.join(' / ')} (${language.and} ${tagsSelected.length - sliceTagsSelected.length} ${language.more})`;
              tooltipText += this.getTruncateTooltip(tagsSelectedWithoutAll.join(', '));
            }
          }
        } else {
          selectedFiltersText = `${language[category.name.toLowerCase()]}: ${tagsSelected.join(' / ')}`;
          tooltipText = `<b>${language[category.name.toLowerCase()]}</b>: ${tagsSelected.join(', ')}`;
        }
      } else {
        tooltipText = `<b>${language[category.name.toLowerCase()]}</b>: ${strings.all}`;
      }

      // add the texts to the arrays to be joined later and returned as a single string each
      if (selectedFiltersText.length > 0) { arrFiltersText.push(selectedFiltersText); }
      if (tooltipText.length > 0) { arrTooltipText.push(`<div class="tooltip-input-item">${tooltipText}</div>`); }
    });

    // return the complete text
    selectedFiltersText = arrFiltersText.join(', ');
    selectedFiltersText = (selectedFiltersText === '') ? strings.all : selectedFiltersText;

    tooltipText = arrTooltipText.join('');
    tooltipText = (tooltipText === '') ? '' : tooltipText;

    return {
      selectedFiltersText,
      tooltipText
    };
  }

  /**
   * Truncates and formats a text for display in a tooltip.
   *
   * @param {string} text - The input text to be truncated and formatted.
   * @returns {string} The truncated and formatted text for use in a tooltip.
   */
  getTruncateTooltip = (text) => {
    const { language } = this.props;
    const MAX_LENGTH_TRUNCATE_TOOLTIP = 76;
    let tooltipText = '';

    if (text) {
      // truncate the text if it exceeds the maximum length
      if (text.length > MAX_LENGTH_TRUNCATE_TOOLTIP) {
        // perform the truncate
        const truncateText = `${text.substring(0, MAX_LENGTH_TRUNCATE_TOOLTIP)}...`;

        // split the original and truncated texts into arrays, considering comma as a separator
        const arrText = text.split(', ');
        const arrTruncateText = truncateText.split(', ');

        // calculate the number of elements NOT shown in the truncated text
        const totalAndMore = arrText.length - arrTruncateText.filter((item) => item !== '...').length;

        // if there are more than 1 hidden elements, construct the text "(and XXX more)"
        const textAndMore = (totalAndMore > 0) ? `(${language.and} ${totalAndMore} ${language.more})` : '';

        // return the complete text
        tooltipText = `${truncateText} ${textAndMore}`;
      } else {
        tooltipText = text;
      }
    }

    return tooltipText;
  }

  /**
   * Handles a click event outside the modal and takes appropriate action based on the 'isMultiple' property.
   * @param {Event} event - The click event object.
   */
  handleClickOutsideModal = (event) => {
    const { isMultiple } = this.props;

    if (isMultiple) {
      this.handleShowPopupAndSubmit(event);
    } else {
      this.handleShowPopup('');
    }
  }

  render() {
    const {
      categories,
      filteredTags,
      language
    } = this.props;

    if(categories && categories.length>0) {
      const { categoriesSlices, categoriesSliceSize } = this.getCategoriesSlices();
      const { selectedFiltersText, tooltipText } = this.getFiltersText();

      return (
        <div className="multiple-tags-input">
          {
            (tooltipText.length > 0) && (
              <div className="tooltip-input" dangerouslySetInnerHTML={{__html: tooltipText}}></div>
            )
          }

          <div className="multiple-tags-input-main-input-wrapper">
            <div className="multiple-tags-input-main-input-label-wrapper">
              <label>{language.categoryFilters}</label>
            </div>
            <div>
              <div
                className={`multiple-tags-input-main-input ${this.props.filterClassName || ''}`}
                onClick={() => this.handleShowPopup('')}
              >
                {selectedFiltersText}
              </div>
            </div>
          </div>
          {this.state.showPopup && (
            <div onClick={this.handleClickOutsideModal} className="multiple-tags-input-popup-glass-panel"/>
          )}
          <div
            className={"multiple-tags-input-popup-wrapper" +
            (categories.length<=categoriesSliceSize ? " small-wrapper" : "") +
            (!this.state.showPopup ? " hide-popup" : "")}
          >
            <div  className="multiple-tags-input-popup-inner-wrapper">
              <div className="multiple-tags-input-popup-inputs-container">
                {categoriesSlices.map((slice, j) => {
                  return (<div key={j} className="multiple-tags-input-popup-inputs-container-slice">
                    {slice.map((item, i) => {
                      return item.name ? (
                        <div key={`box-filter-${i}`} className="box-form islabel box170">
                          <div>
                            <label id={`multiple-tags-category-${item.name.toLowerCase()}`}>{language[item.name.toLowerCase()]}</label>
                          </div>
                          <div>
                            {
                              (this.props.isMultiple) ? (
                                <MultipleCheckboxs
                                  alias={item.name}
                                  options={item.tags}
                                  selectedOptions={filteredTags}
                                  handleChangeOptions={this.handleChangeMultipleCheckboxs}
                                  actionButton={this.state.actionButton}
                                  language={language}
                                />
                              ) : (
                                <Autocomplete
                                  value={filteredTags.length > 0 && filteredTags[j*categoriesSliceSize + i] != '' ? filteredTags[j*categoriesSliceSize + i] : strings.all}
                                  save={strings.all}
                                  id={`au-${j*categoriesSliceSize + i}`}
                                  alias="tag"
                                  select="tag"
                                  idItem="id"
                                  filter={item.tags}
                                  handleSubmit={() => {}}
                                />
                              )
                            }
                          </div>
                        </div>
                      ) : (
                        <div key={`box-filter-${i}`} className="box-form islabel box170">
                          <div>
                            <label>{language.noTags}</label>
                          </div>
                          <div />
                        </div>
                      );
                    })}
                  </div>);
                })}
              </div>
              <div className="multiple-tags-input-popup-buttons-container">
                <div className="big-button cancel-button" onClick={() => this.handleShowPopup('cancel')}>
                  {language.cancel}
                </div>
                <div className="big-button apply-button" onClick={this.handleShowPopupAndSubmit}>
                  {language.apply}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }
}

MultipleTagsInput.defaultProps = {
  isMultiple: false,
};

export default MultipleTagsInput;



// WEBPACK FOOTER //
// ./source/client/components/common/inputs/multipleTagsInput.jsx